<template>
  <section class="workspace-stack">
    <article class="glass-surface workspace-panel workspace-panel--hero">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Códigos</span>
          <h2 class="workspace-title">Gestión de códigos y accesos</h2>
          <p class="workspace-copy">Crea accesos manuales, lotes o cargas CSV y comparte sus links promocionales sin salir de la tabla. Los límites por uso se configuran dentro de cada etapa.</p>
        </div>

        <div class="workspace-actions workspace-actions--compact">
          <button class="workspace-button workspace-button--primary" type="button" @click="toggleCreatePanel(true)">
            <i class="fas fa-plus"></i>
            <span>Crear código</span>
          </button>
        </div>
      </div>

      <div class="workspace-metrics">
        <article class="workspace-metric">
          <small>Total</small>
          <strong>{{ codes.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Activos</small>
          <strong>{{ activeCodes.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Archivados</small>
          <strong>{{ archivedCodes.length }}</strong>
        </article>
        <article class="workspace-metric">
          <small>Links listos</small>
          <strong>{{ activeCodes.length }}</strong>
        </article>
      </div>
    </article>

    <article v-if="createPanelOpen" class="glass-surface workspace-panel workspace-editor-panel">
      <div class="workspace-panel__header workspace-panel__header--spread">
        <div>
          <span class="workspace-kicker">Creación</span>
          <h2 class="workspace-title">Nuevo flujo de códigos</h2>
          <p class="workspace-copy">Elige cómo quieres cargar accesos y luego refrescamos la tabla automáticamente.</p>
        </div>

        <button class="workspace-chip-button" type="button" @click="closePanel">
          <i class="fas fa-times"></i>
          <span>Cerrar</span>
        </button>
      </div>

      <div class="workspace-segmented" role="tablist" aria-label="Modo de creación">
        <button :class="['workspace-segmented__button', creationMode === 'manual' ? 'is-active' : '']" type="button" @click="creationMode = 'manual'">Manual</button>
        <button :class="['workspace-segmented__button', creationMode === 'bulk' ? 'is-active' : '']" type="button" @click="creationMode = 'bulk'">Lote</button>
        <button :class="['workspace-segmented__button', creationMode === 'csv' ? 'is-active' : '']" type="button" @click="creationMode = 'csv'">Importar CSV</button>
      </div>

      <form v-if="creationMode === 'manual'" class="workspace-form workspace-form--two" @submit.prevent="submitManual">
        <label class="workspace-field">
          <span>Código</span>
          <input v-model="manualForm.code" class="workspace-input" type="text" placeholder="VIPFRIENDS" required />
        </label>

        <label class="workspace-field">
          <span>Etiqueta</span>
          <input v-model="manualForm.label" class="workspace-input" type="text" placeholder="Invitados VIP" />
        </label>

        <label class="workspace-field">
          <span>Tipo</span>
          <select v-model="manualForm.code_type" class="workspace-input workspace-select">
            <option value="promo">Promo</option>
            <option value="ambassador">Ambassador</option>
            <option value="other">Otro</option>
          </select>
        </label>

        <label v-if="manualForm.code_type === 'ambassador'" class="workspace-field">
          <span>Nombre del ambassador</span>
          <input v-model="manualForm.ambassador_name" class="workspace-input" type="text" placeholder="Vale PR" />
        </label>

        <label v-if="manualForm.code_type === 'ambassador'" class="workspace-field">
          <span>Instagram</span>
          <input v-model="manualForm.ambassador_instagram" class="workspace-input" type="text" placeholder="@valepr" />
        </label>

        <label v-if="manualForm.code_type === 'ambassador'" class="workspace-field workspace-field--wide">
          <span>Email para login OTP</span>
          <input v-model="manualForm.ambassador_email" class="workspace-input" type="email" placeholder="pr@correo.com" />
          <small class="workspace-field__note">Si luego lo invitas, este correo servira para entrar por OTP y ver solo sus estadisticas.</small>
        </label>

        <div class="workspace-actions workspace-field--wide">
          <button class="workspace-button workspace-button--primary" type="submit" :disabled="saving">
            <i class="fas fa-save"></i>
            <span>{{ saving ? 'Guardando...' : 'Crear código' }}</span>
          </button>
        </div>
      </form>

      <form v-else-if="creationMode === 'bulk'" class="workspace-form workspace-form--two" @submit.prevent="submitBulk">
        <label class="workspace-field">
          <span>Cantidad</span>
          <input v-model.number="bulkForm.quantity" class="workspace-input" type="number" min="1" max="500" required />
        </label>

        <label class="workspace-field">
          <span>Prefijo del código</span>
          <input v-model="bulkForm.prefix" class="workspace-input" type="text" placeholder="VIP" />
        </label>

        <label class="workspace-field workspace-field--wide">
          <span>Prefijo de etiqueta</span>
          <input v-model="bulkForm.label_prefix" class="workspace-input" type="text" placeholder="Wave 1" />
        </label>

        <div class="workspace-actions workspace-field--wide">
          <button class="workspace-button workspace-button--primary" type="submit" :disabled="saving">
            <i class="fas fa-layer-group"></i>
            <span>{{ saving ? 'Generando...' : 'Generar lote' }}</span>
          </button>
        </div>
      </form>

      <form v-else class="workspace-form workspace-form--two" @submit.prevent="submitImport">
        <label class="workspace-field workspace-field--wide">
          <span>Archivo CSV</span>
          <input class="workspace-input" type="file" accept=".csv,text/csv" @change="handleCsvFile" required />
          <small class="workspace-field__note">Usa columnas como `code` o `código`, y opcionalmente `label` o `etiqueta`.</small>
        </label>

        <div class="workspace-actions workspace-field--wide">
          <button class="workspace-button workspace-button--primary" type="submit" :disabled="saving || !csvFile">
            <i class="fas fa-file-import"></i>
            <span>{{ saving ? 'Importando...' : 'Importar CSV' }}</span>
          </button>
        </div>
      </form>
    </article>

    <p v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</p>
    <p v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</p>

    <article class="glass-surface workspace-panel">
      <div class="workspace-panel__header">
        <div>
          <span class="workspace-kicker">Tabla</span>
          <h2 class="workspace-title">Códigos actuales</h2>
        </div>
      </div>

      <div v-if="loading && !codes.length" class="workspace-empty">Cargando códigos...</div>
      <div v-else-if="!codes.length" class="workspace-empty">Todavía no hay códigos creados. Usa el panel superior para cargar el primer acceso.</div>
      <div v-else class="workspace-table-shell">
        <table class="workspace-table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Etiqueta</th>
              <th>Tipo</th>
              <th>Ambassador</th>
              <th>Origen</th>
              <th>Usos</th>
              <th>Estado invitacion</th>
              <th>Status</th>
              <th>Promo link</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="eventCode in codes" :key="eventCode.id">
              <td>
                <div class="workspace-table-title">{{ eventCode.code }}</div>
              </td>
              <td>{{ eventCode.label || 'Sin etiqueta' }}</td>
              <td>{{ typeLabel(eventCode.code_type) }}</td>
              <td>{{ eventCode.ambassador_display || 'Sin asignar' }}</td>
              <td>{{ sourceLabel(eventCode.source) }}</td>
              <td>{{ Number(eventCode.usage_count || 0) }}</td>
              <td>{{ inviteStatusLabel(eventCode.invite_status) }}</td>
              <td>
                <span :class="['workspace-status-pill', eventCode.is_active ? 'is-active' : 'is-off']">{{ eventCode.is_active ? 'Activo' : 'Archivado' }}</span>
              </td>
              <td>
                <a class="workspace-inline-link" :href="fullPromoUrl(eventCode.code)" target="_blank">Abrir link</a>
              </td>
              <td>
                <div class="workspace-inline-actions">
                  <button class="workspace-chip-button" type="button" @click="copyPromoLink(eventCode.code)">
                    <i class="fas fa-copy"></i>
                    <span>Copiar</span>
                  </button>
                  <button v-if="eventCode.code_type === 'ambassador' && eventCode.ambassador_email" class="workspace-chip-button" type="button" @click="inviteAmbassador(eventCode)">
                    <i class="fas fa-paper-plane"></i>
                    <span>Invitar</span>
                  </button>
                  <button v-if="eventCode.is_active" class="workspace-chip-button" type="button" @click="archiveCode(eventCode)">
                    <i class="fas fa-box-archive"></i>
                    <span>Archivar</span>
                  </button>
                  <button v-else class="workspace-chip-button" type="button" @click="reactivateCode(eventCode)">
                    <i class="fas fa-rotate-left"></i>
                    <span>Reactivar</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </section>
</template>

<script>
import { apiService, apiServiceForm } from '../../common/api.services.js'

const defaultManualForm = () => ({
  code: '',
  label: '',
  code_type: 'promo',
  ambassador_name: '',
  ambassador_instagram: '',
  ambassador_email: '',
})

const defaultBulkForm = () => ({
  quantity: 25,
  prefix: '',
  label_prefix: '',
})

export default {
  name: 'CodesView',

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      codes: [],
      successMessage: '',
      errorMessage: '',
      createPanelOpen: false,
      creationMode: 'manual',
      manualForm: defaultManualForm(),
      bulkForm: defaultBulkForm(),
      csvFile: null,
    }
  },

  computed: {
    activeCodes() {
      return this.codes.filter((code) => code.is_active)
    },

    archivedCodes() {
      return this.codes.filter((code) => !code.is_active)
    },
  },

  created() {
    this.getCodes()
  },

  watch: {
    event: {
      deep: true,
      handler() {
        this.getCodes()
      },
    },
  },

  methods: {
    resetMessages() {
      this.successMessage = ''
      this.errorMessage = ''
    },

    resetCreateForms() {
      this.manualForm = defaultManualForm()
      this.bulkForm = defaultBulkForm()
      this.csvFile = null
    },

    toggleCreatePanel(forceOpen) {
      this.resetMessages()
      this.createPanelOpen = typeof forceOpen === 'boolean' ? forceOpen : !this.createPanelOpen
      if (this.createPanelOpen) {
        this.creationMode = 'manual'
      }
    },

    closePanel() {
      this.createPanelOpen = false
      this.resetCreateForms()
    },

    getCodes() {
      if (!this.event || !this.event.id) {
        return
      }

      this.loading = true
      apiService(`/api/host/events/${this.event.id}/codes/`).then((data) => {
        this.codes = Array.isArray(data) ? data : []
      }).finally(() => {
        this.loading = false
      })
    },

    refreshAfterSuccess(message) {
      this.successMessage = message
      this.closePanel()
      this.getCodes()
    },

    fullPromoUrl(code) {
      return `${window.location.origin}/event/${this.event.slug}?co=${encodeURIComponent(code)}&wanted=true`
    },

    sourceLabel(source) {
      if (Number(source) === 2) {
        return 'Lote'
      }
      if (Number(source) === 3) {
        return 'CSV'
      }
      return 'Manual'
    },

    typeLabel(value) {
      if (value === 'ambassador') return 'Ambassador'
      if (value === 'other') return 'Otro'
      return 'Promo'
    },

    inviteStatusLabel(value) {
      if (value === 'claimed') return 'Conectado'
      if (value === 'invited') return 'Invitado'
      return 'Generico'
    },

    copyPromoLink(code) {
      navigator.clipboard.writeText(this.fullPromoUrl(code)).then(() => {
        this.successMessage = `Link de ${code} copiado.`
      })
    },

    submitManual() {
      this.resetMessages()
      this.saving = true

      apiService(`/api/host/events/${this.event.id}/codes/`, 'POST', {
        code: this.manualForm.code,
        label: this.manualForm.label,
        is_active: true,
        code_type: this.manualForm.code_type,
        ambassador_name: this.manualForm.ambassador_name,
        ambassador_instagram: this.manualForm.ambassador_instagram,
        ambassador_email: this.manualForm.ambassador_email,
      }).then((data) => {
        if (data && !data.err) {
          this.refreshAfterSuccess('Codigo creado.')
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo crear el código.'
      }).finally(() => {
        this.saving = false
      })
    },

    submitBulk() {
      this.resetMessages()
      this.saving = true

      apiService(`/api/host/events/${this.event.id}/codes/bulk/`, 'POST', {
        quantity: this.bulkForm.quantity,
        prefix: this.bulkForm.prefix,
        label_prefix: this.bulkForm.label_prefix,
      }).then((data) => {
        if (data && !data.err) {
          this.refreshAfterSuccess('Lote generado.')
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo generar el lote.'
      }).finally(() => {
        this.saving = false
      })
    },

    handleCsvFile(event) {
      const files = event && event.target ? event.target.files : null
      this.csvFile = files && files.length ? files[0] : null
    },

    submitImport() {
      if (!this.csvFile) {
        this.errorMessage = 'Selecciona un archivo CSV.'
        return
      }

      this.resetMessages()
      this.saving = true

      const formData = new FormData()
      formData.append('file', this.csvFile)

      apiServiceForm(`/api/host/events/${this.event.id}/codes/import/`, 'POST', formData).then((data) => {
        if (data && !data.err) {
          this.refreshAfterSuccess(`Importación lista. ${data.created || 0} códigos nuevos.`)
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo importar el CSV.'
      }).finally(() => {
        this.saving = false
      })
    },

    archiveCode(eventCode) {
      this.resetMessages()
      apiService(`/api/host/events/codes/${eventCode.id}/`, 'DELETE').then((data) => {
        if (data && data.err) {
          this.errorMessage = data.err
          return
        }

        this.successMessage = `Codigo ${eventCode.code} archivado.`
        this.getCodes()
      })
    },

    reactivateCode(eventCode) {
      this.resetMessages()
      apiService(`/api/host/events/codes/${eventCode.id}/`, 'PATCH', {
        code: eventCode.code,
        label: eventCode.label,
        is_active: true,
      }).then((data) => {
        if (data && !data.err) {
          this.successMessage = `Codigo ${eventCode.code} reactivado.`
          this.getCodes()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo reactivar el código.'
      })
    },

    inviteAmbassador(eventCode) {
      this.resetMessages()
      apiService(`/api/host/events/codes/${eventCode.id}/invite/`, 'POST').then((data) => {
        if (data && !data.err) {
          this.successMessage = `Invitacion enviada a ${eventCode.ambassador_email}.`
          this.getCodes()
          return
        }

        this.errorMessage = (data && data.err) || 'No se pudo enviar la invitacion.'
      })
    },

  },
}
</script>

<style scoped>
.workspace-panel--hero {
  display: grid;
  gap: 18px;
}

.workspace-panel__header--spread {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.workspace-editor-panel {
  display: grid;
  gap: 18px;
}

.workspace-segmented {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
}

.workspace-segmented__button {
  border: 1px solid rgba(223, 214, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
  color: var(--text-soft);
  border-radius: 999px;
  padding: 10px 16px;
  font: inherit;
}

.workspace-segmented__button.is-active {
  color: var(--text-main);
  background: rgba(203, 255, 232, 0.08);
  border-color: rgba(203, 255, 232, 0.18);
}

.workspace-table-shell {
  overflow: auto;
}

.workspace-table {
  width: 100%;
  border-collapse: collapse;
}

.workspace-table th,
.workspace-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(223, 214, 255, 0.08);
  text-align: left;
  color: var(--text-soft);
  vertical-align: top;
}

.workspace-table th {
  color: var(--text-main);
  font-size: 0.75rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.workspace-table-title {
  color: var(--text-main);
  font-weight: 700;
}

.workspace-inline-link {
  color: #cbffe8;
}

.workspace-inline-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 900px) {
  .workspace-panel__header--spread {
    display: grid;
  }
}
</style>
